import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ResponseError } from 'services/api/base';
import {
  MeteorologyViewMode,
  ObservedDataPeriodMode,
} from 'types/Meteorology/Fields/ObservedData/getOptions';
import { CustomAlert } from '../../components/AlertsCollection';

export type Variable = { name: string, label: string };

// Re-export para preservar contrato de imports (`from '...reducer'`) usados em outros arquivos.
export type { MeteorologyViewMode };

export interface ObservedDataConsultSnapshot {
  viewMode: MeteorologyViewMode;
  variable?: Variable;
  selectedPeriod?: string;
  plotArea?: string;
  startDate?: number;
  endDate?: number;
}

export interface ObservedDataControlPanelOptions {
  startDate?: number;
  endDate?: number;
  plotArea?: string;
  selectedPeriod?: string,
  variable?: Variable,
  anomaly: boolean,
  obsImageBase64: string,
  climatologyImageBase64: string,
  anomalyImageBase64: string,
  heatmapImageBase64: string,
  imagesAreLoading: boolean,
  errorTitle: string,
  errorMessage: string,
  mode: ObservedDataPeriodMode;
  viewMode: MeteorologyViewMode;
  lastConsulted?: ObservedDataConsultSnapshot;
}

const initialState: ObservedDataControlPanelOptions = {
  startDate: undefined,
  endDate: undefined,
  selectedPeriod: undefined,
  variable: undefined,
  plotArea: undefined,
  anomaly: false,
  obsImageBase64: '',
  climatologyImageBase64: '',
  anomalyImageBase64: '',
  heatmapImageBase64: '',
  imagesAreLoading: false,
  errorTitle: '',
  errorMessage: '',
  mode: 'preset',
  viewMode: 'spatial',
  lastConsulted: undefined,
};

export const meteorologyFieldsObservedDataControlPanelSlice = createSlice({
  name: 'meteorologyFieldsObservedDataControlPanel',
  initialState,
  reducers: {
    reset: (state) => {
      state.startDate = initialState.startDate;
      state.endDate = initialState.endDate;
      state.plotArea = initialState.plotArea;
      state.selectedPeriod = initialState.selectedPeriod;
      state.anomaly = initialState.anomaly;
      state.obsImageBase64 = initialState.obsImageBase64;
      state.climatologyImageBase64 = initialState.climatologyImageBase64;
      state.anomalyImageBase64 = initialState.anomalyImageBase64;
      state.heatmapImageBase64 = initialState.heatmapImageBase64;
      state.imagesAreLoading = initialState.imagesAreLoading;
      state.errorTitle = initialState.errorTitle;
      state.errorMessage = initialState.errorMessage;
    },
    setSelectedPeriod: (state, action: PayloadAction<string>) => {
      state.selectedPeriod = action.payload;
      state.startDate = undefined;
      state.endDate = undefined;
    },
    setVariable: (state, action: PayloadAction<Variable>) => {
      // O auto-switch para 'spatial' quando a variavel nao suporta heatmap fica no hook
      // (`useObservedDataControlPanel`), que tem acesso a `supportedVisualizations` vinda do
      // backend via `painel-options`. Reducer permanece simples e desacoplado dessa matriz.
      state.variable = action.payload;
      state.selectedPeriod = initialState.selectedPeriod;
      state.startDate = initialState.startDate;
      state.endDate = initialState.endDate;
    },
    setStartPeriod: (state, action: PayloadAction<number | undefined>) => {
      state.startDate = action.payload;
      state.selectedPeriod = initialState.selectedPeriod;
    },
    setEndPeriod: (state, action: PayloadAction<number | undefined>) => {
      state.endDate = action.payload;
      state.selectedPeriod = initialState.selectedPeriod;
    },
    clearDates: (state) => {
      state.startDate = undefined;
      state.endDate = undefined;
    },
    setPlotArea: (state, action: PayloadAction<string>) => {
      state.plotArea = action.payload;
    },
    setAnomaly: (state, action: PayloadAction<boolean>) => {
      state.anomaly = action.payload;
    },
    setObsImageString: (state, action: PayloadAction<string>) => {
      state.obsImageBase64 = action.payload;
    },
    setClimatologyImageString: (state, action: PayloadAction<string>) => {
      state.climatologyImageBase64 = action.payload;
    },
    setAnomalyImageString: (state, action: PayloadAction<string>) => {
      state.anomalyImageBase64 = action.payload;
    },
    setHeatmapImageString: (state, action: PayloadAction<string>) => {
      state.heatmapImageBase64 = action.payload;
    },
    setImageLoading: (state, action: PayloadAction<boolean>) => {
      state.imagesAreLoading = action.payload;
    },
    setErrorAlert: (state, action: PayloadAction<ResponseError | undefined>) => {
      state.errorTitle = action.payload?.data ?? '';
      state.errorMessage = action.payload?.description ?? '';
    },
    setMode: (state, action: PayloadAction<ObservedDataPeriodMode>) => {
      state.mode = action.payload;
    },
    setViewMode: (state, action: PayloadAction<MeteorologyViewMode>) => {
      state.viewMode = action.payload;
    },
    setLastConsulted: (state, action: PayloadAction<ObservedDataConsultSnapshot>) => {
      state.lastConsulted = action.payload;
    },
  },
});

export const selectMFControlPanelObservedDataPlotArea = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.plotArea
);
export const selectMFControlPanelObservedDataSelectedPeriod = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.selectedPeriod
);
export const selectMFControlPanelObservedDataVariable = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.variable
);
export const selectMFControlPanelObservedDataStartPeriod = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.startDate
);
export const selectMFControlPanelObservedDataEndPeriod = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.endDate
);
export const selectMFControlPanelObservedDataAnomaly = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.anomaly
);
export const selectMFControlPanelObservedDataObsImageString = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.obsImageBase64
);
export const selectMFControlPanelObservedDataClimatologyImageString = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.climatologyImageBase64
);
export const selectMFControlPanelObservedDataAnomalyImageString = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.anomalyImageBase64
);
export const selectMFControlPanelObservedDataImageLoading = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.imagesAreLoading
);
export const selectMFControlPanelObservedDataErrorAlert = (state: any): CustomAlert[] => (
  state.meteorologyFieldsObservedDataControlPanel.errorTitle === '' ? [] : [
    {
      title: state.meteorologyFieldsObservedDataControlPanel.errorTitle,
      message: state.meteorologyFieldsObservedDataControlPanel.errorMessage,
      variant: 'danger',
    },
  ]
);
export const selectMFControlPanelObservedDataMode = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.mode
);
export const selectMFControlPanelObservedDataViewMode = (state: any): MeteorologyViewMode => (
  state.meteorologyFieldsObservedDataControlPanel.viewMode
);
export const selectMFControlPanelObservedDataHeatmapImageString = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.heatmapImageBase64
);
export const selectMFControlPanelObservedDataLastConsulted = (state: any) => (
  state.meteorologyFieldsObservedDataControlPanel.lastConsulted as
    ObservedDataConsultSnapshot | undefined
);

export const {
  reset,
  setSelectedPeriod,
  setVariable,
  setStartPeriod,
  setEndPeriod,
  clearDates,
  setPlotArea,
  setAnomaly,
  setObsImageString,
  setClimatologyImageString,
  setAnomalyImageString,
  setHeatmapImageString,
  setImageLoading,
  setErrorAlert,
  setMode,
  setViewMode,
  setLastConsulted,
} = meteorologyFieldsObservedDataControlPanelSlice.actions;

export default meteorologyFieldsObservedDataControlPanelSlice.reducer;
